/**
 * Unified Sport Context Hook
 * Combines TeamProvider context with custom event system for platform-wide sport/team reactivity
 *
 * Use this hook in any component that needs to react to sport/team changes from Settings
 */

import { useState, useEffect, useCallback, useMemo } from 'react';

// Sport code to category mapping — all 33 NatStat service codes
const SPORT_CATEGORIES = {
  // Basketball (17)
  'NBA': 'basketball',
  'GL': 'basketball',
  'WNBA': 'basketball',
  'MBB': 'basketball',
  'MB2': 'basketball',
  'MB3': 'basketball',
  'MBIA': 'basketball',
  'MBJC': 'basketball',
  'WBB': 'basketball',
  'WB2': 'basketball',
  'WB3': 'basketball',
  'WBIA': 'basketball',
  'WBJC': 'basketball',
  'WBPro': 'basketball',
  'EuroBB': 'basketball',
  'AsiaBB': 'basketball',
  'AmerBB': 'basketball',
  'EURO': 'basketball', // legacy alias
  // Baseball (5)
  'MLB': 'baseball',
  'MiLB': 'baseball',
  'KBO': 'baseball',
  'NPB': 'baseball',
  'CBB': 'baseball',
  // Hockey (5)
  'NHL': 'hockey',
  'MiLH': 'hockey',
  'MHK': 'hockey',
  'KHL': 'hockey',
  'WHK': 'hockey',
  // Football (2)
  'PFB': 'football',
  'CFB': 'football',
  'NFL': 'football', // legacy alias
  // Soccer (1)
  'FC': 'soccer',
  // Golf (3)
  'PGA': 'golf',
  'LPGA': 'golf',
  'CGF': 'golf',
  // Formula 1
  'F1': 'f1',
};

// Sport code to display name — all 33 NatStat service codes
const SPORT_DISPLAY_NAMES = {
  // Basketball
  'NBA': 'NBA',
  'GL': 'G League',
  'WNBA': 'WNBA',
  'MBB': 'Men\'s Basketball D1',
  'MB2': 'Men\'s Basketball D2',
  'MB3': 'Men\'s Basketball D3',
  'MBIA': 'Men\'s Basketball NAIA',
  'MBJC': 'Men\'s Basketball JUCO',
  'WBB': 'Women\'s Basketball D1',
  'WB2': 'Women\'s Basketball D2',
  'WB3': 'Women\'s Basketball D3',
  'WBIA': 'Women\'s Basketball NAIA',
  'WBJC': 'Women\'s Basketball JUCO',
  'WBPro': 'Women\'s Pro Basketball',
  'EuroBB': 'European Basketball',
  'AsiaBB': 'Asian Basketball',
  'AmerBB': 'Americas Basketball',
  'EURO': 'EuroLeague',
  // Baseball
  'MLB': 'MLB',
  'MiLB': 'Minor League Baseball',
  'KBO': 'Korean Baseball',
  'NPB': 'Japanese Baseball',
  'CBB': 'College Baseball',
  // Hockey
  'NHL': 'NHL',
  'MiLH': 'Minor League Hockey',
  'MHK': 'Men\'s College Hockey',
  'KHL': 'KHL',
  'WHK': 'Women\'s College Hockey',
  // Football
  'PFB': 'Pro Football',
  'CFB': 'College Football',
  'NFL': 'NFL',
  // Soccer
  'FC': 'Football Club / Soccer',
  // Golf
  'PGA': 'PGA Tour',
  'LPGA': 'LPGA Tour',
  'CGF': 'College Golf',
  // Formula 1
  'F1': 'Formula 1',
};

// Default data endpoints by sport
const SPORT_DATA_ENDPOINTS = {
  'MLB': {
    teams: null, // Hardcoded in Settings
    players: '/api/mlb/players',
    stats: '/api/mlb/stats',
    statcast: '/api/statcast',
  },
  'CBB': {
    teams: 'https://slam-all-python-api.replit.app/CBB/transfer-portal/players?schema=CBB',
    players: 'https://slam-all-python-api.replit.app/CBB/transfer-portal/players',
    stats: null,
  },
  'MBB': {
    teams: 'https://slam-all-python-api.replit.app/MBB/team-stats-mia/',
    players: 'https://slam-all-python-api.replit.app/MBB/players',
    stats: '/api/mbb/stats',
  },
  'WBB': {
    teams: null, // From local JSON
    players: '/api/wbb/players',
    stats: '/api/wbb/stats',
  },
  'NBA': {
    teams: '/api/nba/teams',
    players: '/api/nba/players',
    stats: '/api/nba/stats',
  },
  'WNBA': {
    teams: '/api/wnba/teams',
    players: '/api/wnba/players',
    stats: '/api/wnba/stats',
  },
  'EURO': {
    teams: null, // From local EUROLEAGUE_TEAMS constant
    players: null, // From local EUROLEAGUE_ROSTERS constant
    stats: null,
  },
  'GL': {
    teams: '/api/gleague/teams',
    players: '/api/gleague/players',
    stats: '/api/gleague/stats',
  },
  // Football — NatStat BigQuery data
  'PFB': {
    teams: '/api/natstat/teams/PFB',
    players: '/api/natstat/players/PFB',
    stats: null, // Via Code Mode queryFootballData
  },
  'CFB': {
    teams: '/api/natstat/teams/CFB',
    players: '/api/natstat/players/CFB',
    stats: null,
  },
  // Soccer — NatStat BigQuery data
  'FC': {
    teams: '/api/natstat/teams/FC',
    players: '/api/natstat/players/FC',
    stats: null,
  },
  // Hockey — NatStat BigQuery data
  'NHL': {
    teams: '/api/natstat/teams/NHL',
    players: '/api/natstat/players/NHL',
    stats: null,
  },
  'MiLH': {
    teams: '/api/natstat/teams/MiLH',
    players: '/api/natstat/players/MiLH',
    stats: null,
  },
  // Formula 1 — BigQuery formula1 dataset (no NatStat)
  'F1': {
    teams: null, // Constructors via Code Mode
    players: null, // Drivers via Code Mode
    stats: null,
  },
  // Golf — BigQuery pga_golf dataset
  'PGA': {
    teams: null, // Individual sport
    players: null, // Athletes via Code Mode
    stats: null,
  },
};

/**
 * Get stored sport/team from localStorage/sessionStorage
 */
function getStoredContext() {
  if (typeof window === 'undefined') {
    return { sport: null, team: null };
  }

  try {
    const sportStr = localStorage.getItem('selectedSport') || sessionStorage.getItem('slamSport');
    const teamStr = localStorage.getItem('selectedTeam') || sessionStorage.getItem('slamTeam');

    return {
      sport: sportStr ? JSON.parse(sportStr) : null,
      team: teamStr ? JSON.parse(teamStr) : null,
    };
  } catch (e) {
    console.warn('Failed to parse stored sport context:', e);
    return { sport: null, team: null };
  }
}

/**
 * Hook to access sport context with reactive updates
 * @param {Object} options - Configuration options
 * @param {Function} options.onSportChange - Callback when sport changes
 * @param {Function} options.onTeamChange - Callback when team changes
 * @returns {Object} Sport context with utilities
 */
export function useSportContext(options = {}) {
  const { onSportChange, onTeamChange } = options;

  // Initialize from stored values
  const [context, setContext] = useState(() => getStoredContext());
  const [isLoading, setIsLoading] = useState(false);

  // Handle settings change event
  const handleSettingsChange = useCallback((e) => {
    const { sport, team, previousSport, previousTeam } = e.detail || {};

    setContext((prev) => {
      const newContext = {
        sport: sport || prev.sport,
        team: team || prev.team,
      };

      // Trigger callbacks if values changed
      if (sport && (!previousSport || sport.code !== previousSport.code)) {
        onSportChange?.(sport, previousSport);
      }
      if (team && (!previousTeam || team.team_id !== previousTeam.team_id)) {
        onTeamChange?.(team, previousTeam);
      }

      return newContext;
    });
  }, [onSportChange, onTeamChange]);

  // Handle storage events (cross-tab sync)
  const handleStorageChange = useCallback((e) => {
    if (e.key === 'selectedSport' || e.key === 'selectedTeam') {
      setContext(getStoredContext());
    }
  }, []);

  // Subscribe to events
  useEffect(() => {
    window.addEventListener('slamSettingsChanged', handleSettingsChange);
    window.addEventListener('storage', handleStorageChange);

    // Hydrate from storage on mount
    setContext(getStoredContext());

    return () => {
      window.removeEventListener('slamSettingsChanged', handleSettingsChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [handleSettingsChange, handleStorageChange]);

  // Derived values
  const sportCode = context.sport?.code || null;
  const sportCategory = sportCode ? SPORT_CATEGORIES[sportCode] : null;
  const sportName = sportCode ? SPORT_DISPLAY_NAMES[sportCode] : null;
  const teamId = context.team?.team_id || context.team?.id || null;
  const teamName = context.team?.team_name || context.team?.name || null;

  // Check sport category
  const isBaseball = sportCategory === 'baseball';
  const isBasketball = sportCategory === 'basketball';
  const isFootball = sportCategory === 'football';
  const isHockey = sportCategory === 'hockey';
  const isSoccer = sportCategory === 'soccer';
  const isGolf = sportCategory === 'golf';
  const isF1 = sportCategory === 'f1';
  const isPro = ['MLB', 'NBA', 'WNBA', 'NFL', 'NHL', 'PFB', 'PGA', 'LPGA', 'GL', 'WBPro', 'KBO', 'NPB', 'KHL', 'F1'].includes(sportCode);
  const isCollege = ['CBB', 'MBB', 'MB2', 'MB3', 'MBIA', 'MBJC', 'WBB', 'WB2', 'WB3', 'WBIA', 'WBJC', 'CFB', 'MHK', 'WHK', 'CGF'].includes(sportCode);

  // Get endpoints for current sport
  const endpoints = useMemo(() => {
    if (!sportCode) return null;
    return SPORT_DATA_ENDPOINTS[sportCode] || null;
  }, [sportCode]);

  // Utility to fetch sport-specific data
  const fetchSportData = useCallback(async (dataType) => {
    if (!sportCode || !endpoints) {
      console.warn('No sport selected or endpoints not configured');
      return null;
    }

    const endpoint = endpoints[dataType];
    if (!endpoint) {
      console.warn(`No ${dataType} endpoint for ${sportCode}`);
      return null;
    }

    setIsLoading(true);
    try {
      const response = await fetch(endpoint);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error(`Failed to fetch ${dataType} for ${sportCode}:`, error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [sportCode, endpoints]);

  return {
    // Current context
    sport: context.sport,
    team: context.team,
    sportCode,
    sportCategory,
    sportName,
    teamId,
    teamName,

    // Convenience flags
    isBaseball,
    isBasketball,
    isFootball,
    isHockey,
    isSoccer,
    isGolf,
    isF1,
    isPro,
    isCollege,
    isLoading,
    hasSelection: !!sportCode && !!teamId,

    // Endpoints and data fetching
    endpoints,
    fetchSportData,

    // Constants for consumers
    SPORT_CATEGORIES,
    SPORT_DISPLAY_NAMES,
    SPORT_DATA_ENDPOINTS,
  };
}

/**
 * Hook specifically for Statcast data (MLB only)
 */
export function useStatcastContext() {
  const { sport, team, sportCode, isPro } = useSportContext();

  const isStatcastEnabled = sportCode === 'MLB';
  const statcastTeamId = isStatcastEnabled ? team?.team_id : null;

  const fetchPitcherData = useCallback(async (pitcherId) => {
    if (!isStatcastEnabled) {
      console.warn('Statcast only available for MLB');
      return null;
    }

    try {
      const response = await fetch(`/api/statcast/pitcher/${pitcherId}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch pitcher data:', error);
      return null;
    }
  }, [isStatcastEnabled]);

  const fetchBatterData = useCallback(async (batterId) => {
    if (!isStatcastEnabled) {
      console.warn('Statcast only available for MLB');
      return null;
    }

    try {
      const response = await fetch(`/api/statcast/batter/${batterId}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch batter data:', error);
      return null;
    }
  }, [isStatcastEnabled]);

  return {
    isStatcastEnabled,
    statcastTeamId,
    fetchPitcherData,
    fetchBatterData,
  };
}

/**
 * Hook for basketball-specific context (NBA, WNBA, MBB, WBB)
 */
export function useBasketballContext() {
  const { sport, team, sportCode, isBasketball, isPro, isCollege } = useSportContext();

  const isNBA = sportCode === 'NBA';
  const isWNBA = sportCode === 'WNBA';
  const isMBB = sportCode === 'MBB';
  const isWBB = sportCode === 'WBB';

  const fetchPlayerStats = useCallback(async (playerId) => {
    if (!isBasketball) {
      console.warn('Basketball context required');
      return null;
    }

    const endpoint = isPro
      ? `/api/${sportCode.toLowerCase()}/player/${playerId}/stats`
      : `https://slam-all-python-api.replit.app/${sportCode}/player/${playerId}/stats`;

    try {
      const response = await fetch(endpoint);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch basketball player stats:', error);
      return null;
    }
  }, [isBasketball, isPro, sportCode]);

  return {
    isBasketball,
    isNBA,
    isWNBA,
    isMBB,
    isWBB,
    isPro,
    isCollege,
    fetchPlayerStats,
  };
}

export default useSportContext;
